<template>
  <div
    ref="rootRef"
    class="area-screenshot-overlay"
    @mousedown="onMouseDown"
    @mousemove="onMouseMove"
    @mouseup="onMouseUp"
  >
    <!-- Full-screen dim before the user starts dragging. Once a selection
         exists the per-rect `box-shadow` on `.area-screenshot-overlay__selection`
         takes over the dim instead, giving a single cut-out window. -->
    <div v-if="!hasSelection" class="area-screenshot-overlay__dim" />

    <div
      v-if="hasSelection"
      class="area-screenshot-overlay__selection"
      :style="selectionStyle"
    />

    <div class="area-screenshot-overlay__hint">
      <v-icon small color="white" class="mr-2">mdi-selection-drag</v-icon>
      <span>{{ hintText }}</span>
      <span class="area-screenshot-overlay__hint-divider">·</span>
      <kbd>Esc</kbd>
      <span class="ml-1">aby anulować</span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { AreaRect, } from '@/components/calculator/results/feedback/useScreenshotCapture';

type Point = { x: number; y: number };

export default defineComponent({
  name: 'AreaScreenshotOverlay',
  emits: {
    complete: (_rect: AreaRect) => true,
    cancel: () => true,
  },
  setup(_, { emit, }) {
    const rootRef = ref<HTMLElement | null>(null);
    const start = ref<Point | null>(null);
    const current = ref<Point | null>(null);
    const isDragging = ref<boolean>(false);

    const hasSelection = computed<boolean>(() => Boolean(start.value && current.value));

    const selectionRect = computed<AreaRect | null>(() => {
      if (!start.value || !current.value) return null;
      const x = Math.min(start.value.x, current.value.x);
      const y = Math.min(start.value.y, current.value.y);
      const width = Math.abs(current.value.x - start.value.x);
      const height = Math.abs(current.value.y - start.value.y);
      return { x, y, width, height, };
    });

    // No explicit generic — TS otherwise rejects the `{}` branch under
    // strict mode because the union ends up shaped as `{ left?: undefined }
    // | { left: string }` and `Record<string, string>` doesn't tolerate the
    // optional-undefined arm. Vue's :style binding is happy with either.
    const selectionStyle = computed(() => {
      const rect = selectionRect.value;
      if (!rect) return {};
      return {
        left: `${rect.x}px`,
        top: `${rect.y}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
      };
    });

    const hintText = computed<string>(() => {
      return isDragging.value || hasSelection.value
        ? 'Zwolnij, aby zrobić screenshot'
        : 'Przeciągnij, aby zaznaczyć obszar';
    });

    const onMouseDown = (event: MouseEvent) => {
      // Only the left button starts a selection; right-click / middle-click
      // shouldn't accidentally trigger a capture.
      if (event.button !== 0) return;
      isDragging.value = true;
      start.value = { x: event.clientX, y: event.clientY, };
      current.value = { x: event.clientX, y: event.clientY, };
    };

    const onMouseMove = (event: MouseEvent) => {
      if (!isDragging.value) return;
      current.value = { x: event.clientX, y: event.clientY, };
    };

    const onMouseUp = () => {
      if (!isDragging.value) return;
      isDragging.value = false;
      const rect = selectionRect.value;
      // < 5px on either axis is almost certainly an unintended click rather
      // than a real selection — treat as cancel so the user can try again.
      if (!rect || rect.width < 5 || rect.height < 5) {
        emit('cancel');
        return;
      }
      emit('complete', rect);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        emit('cancel');
      }
    };

    onMounted(() => {
      window.addEventListener('keydown', onKeyDown);
      // Teleport the overlay to <body>. Without this `position: fixed`
      // anchors to the nearest ancestor that creates a containing block —
      // and the simulation listing uses `-webkit-transform: scale()` (see
      // services/utils/ZoomStyles.ts) which does exactly that. The result
      // is an overlay that only fills the toolbar strip instead of the
      // viewport. Re-parenting to <body> sidesteps the issue entirely.
      // Vue 2 doesn't ship Teleport in 2.7's runtime, so we manipulate the
      // DOM directly — Vue's patch system uses the element's current parent
      // at unmount, so cleanup still works.
      //
      // TODO(vue3): once the app is on Vue 3, replace this manual DOM
      // re-parenting with `<Teleport to="body">` — the patch behavior
      // we rely on here (`el.parentNode` at unmount) is a Vue 2.7
      // implementation detail and not guaranteed across major bumps.
      if (rootRef.value && rootRef.value.parentNode !== document.body) {
        document.body.appendChild(rootRef.value);
      }
    });
    onBeforeUnmount(() => {
      window.removeEventListener('keydown', onKeyDown);
      // No explicit DOM removal — Vue 2's patch algorithm reads the
      // element's current parentNode (which we set to <body> on mount)
      // when unmounting, so body.removeChild() runs implicitly. Doing it
      // manually here would race against Vue and double-remove.
    });

    return {
      rootRef,
      hasSelection,
      selectionStyle,
      hintText,
      onMouseDown,
      onMouseMove,
      onMouseUp,
    };
  },
});
</script>

<style scoped lang="scss">
.area-screenshot-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  cursor: crosshair;
  user-select: none;

  &__dim {
    position: absolute;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }

  // The selection rect itself is transparent — what darkens the rest of
  // the page is its massive outer box-shadow, which paints a single dim
  // halo around the rect without needing four separate "frame" divs.
  &__selection {
    position: absolute;
    border: 2px solid #ffffff;
    background-color: transparent;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.4);
    pointer-events: none;
  }

  &__hint {
    position: absolute;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.75);
    color: #ffffff;
    padding: 8px 14px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 500;
    pointer-events: none;
    white-space: nowrap;

    kbd {
      background-color: rgba(255, 255, 255, 0.18);
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 4px;
      padding: 1px 6px;
      font-family: inherit;
      font-size: 11px;
    }
  }

  &__hint-divider {
    margin: 0 10px;
    opacity: 0.5;
  }
}
</style>
